<template>
  <div v-if="hasLocation" class="tour-map-wrapper">
    <button @click="showMap = !showMap" class="map-toggle" :class="{ active: showMap }">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
      </svg>
    </button>
    <div v-show="showMap" class="map-container" ref="mapContainer">
      <button @click="showMap = false" class="map-close">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
        </svg>
      </button>
      <div ref="mapElement" class="map-element"></div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, nextTick } from 'vue'

const props = defineProps<{
  lat?: number | null
  lng?: number | null
  title?: string
  googleMapsApiKey?: string | null
}>()

const showMap = ref(false)
const mapContainer = ref<HTMLElement>()
const mapElement = ref<HTMLElement>()
let mapInstance: any = null

const hasLocation = computed(() => 
  props.lat != null && props.lng != null && props.lat !== 0 && props.lng !== 0
)

watch(showMap, async (val) => {
  if (val && !mapInstance) {
    await nextTick()
    initMap()
  }
})

async function initMap() {
  if (!mapElement.value || !props.lat || !props.lng) return

  // Use Google Maps if API key provided, otherwise Leaflet/OSM
  if (props.googleMapsApiKey) {
    initGoogleMap()
  } else {
    initLeafletMap()
  }
}

async function initLeafletMap() {
  const L = await import('leaflet')
  await import('leaflet/dist/leaflet.css')
  
  if (!mapElement.value) return
  
  mapInstance = L.map(mapElement.value).setView([props.lat!, props.lng!], 15)
  
  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    maxZoom: 19,
  }).addTo(mapInstance)

  // Custom marker
  const icon = L.divIcon({
    html: `<svg width="32" height="40" viewBox="0 0 32 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M16 0C7.16 0 0 7.16 0 16c0 12 16 24 16 24s16-12 16-24C32 7.16 24.84 0 16 0zm0 22c-3.31 0-6-2.69-6-6s2.69-6 6-6 6 2.69 6 6-2.69 6-6 6z" fill="#3b82f6"/>
      <circle cx="16" cy="16" r="4" fill="white"/>
    </svg>`,
    iconSize: [32, 40],
    iconAnchor: [16, 40],
    className: 'tour-map-marker',
  })

  L.marker([props.lat!, props.lng!], { icon }).addTo(mapInstance)
    .bindPopup(`<b>${props.title || 'Tour Location'}</b>`)

  // Fix Leaflet rendering in hidden container
  setTimeout(() => mapInstance?.invalidateSize(), 100)
}

function initGoogleMap() {
  // Load Google Maps script dynamically
  if (!(window as any).google?.maps) {
    const script = document.createElement('script')
    script.src = `https://maps.googleapis.com/maps/api/js?key=${props.googleMapsApiKey}`
    script.onload = () => createGoogleMap()
    document.head.appendChild(script)
  } else {
    createGoogleMap()
  }
}

function createGoogleMap() {
  if (!mapElement.value || !(window as any).google?.maps) return
  const google = (window as any).google
  
  const map = new google.maps.Map(mapElement.value, {
    center: { lat: props.lat!, lng: props.lng! },
    zoom: 15,
    mapTypeControl: false,
    streetViewControl: false,
  })

  new google.maps.Marker({
    position: { lat: props.lat!, lng: props.lng! },
    map,
    title: props.title || 'Tour Location',
  })

  mapInstance = map
}
</script>

<style scoped>
.tour-map-wrapper {
  position: absolute;
  top: 160px;
  right: 16px;
  z-index: 20;
}

.map-toggle {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(8px);
  transition: all 0.2s;
}

.map-toggle:hover, .map-toggle.active {
  background: rgba(0, 0, 0, 0.8);
  border-color: rgba(59, 130, 246, 0.5);
}

.map-container {
  position: absolute;
  top: 48px;
  right: 0;
  width: 320px;
  height: 240px;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.15);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  animation: fadeIn 0.2s ease;
}

.map-close {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1000;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.7);
  border: none;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.map-element {
  width: 100%;
  height: 100%;
}

:deep(.tour-map-marker) {
  background: none !important;
  border: none !important;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
}

@media (max-width: 768px) {
  .tour-map-wrapper {
    right: 12px;
    top: 145px;
  }

  .map-toggle {
    width: 34px;
    height: 34px;
  }

  .map-container {
    width: 260px;
    height: 200px;
  }
}

@media (max-width: 480px) {
  .map-container {
    width: calc(100vw - 24px);
    height: 180px;
    right: -12px;
  }
}
</style>
